import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AnimatePresence } from "framer-motion";

import { Notice, NoticeStack, NoticeSpinner, NoticeDot } from "./Notice";
import { useGameState } from "../GameStateContext";
import { useWebSocket } from "../useWebSocket";

/**
 * Every app-level transient notification, rendered into a single top-centre
 * stack so they share one anchor, one z-index, and one uniform card look
 * (see Notice). Connection status is all that's left: the service worker no
 * longer caches the app, so there's no stale build to prompt anyone about.
 *
 * Connection priority (top-most wins):
 *   1. Device offline (navigator.onLine === false)
 *   2. WS reconnecting (auto-reconnect loop in flight)
 *   3. WS failed (gave up; manual retry)
 *   4. Brief "Reconnected" pulse (reconnecting → connected)
 *   5. Kicked (only meaningful on an active screen; manual dismiss)
 *
 * Connection notices are hidden on the home screen (`enter_room_code`) because
 * EnterRoomCode surfaces its own connect errors there.
 */

function useConnectionNotice(): ReactNode {
    const { state, set } = useGameState();
    const { retryReconnect } = useWebSocket();

    const [online, setOnline] = useState(() =>
        typeof navigator === "undefined" ? true : navigator.onLine
    );
    const [reconnectedFlash, setReconnectedFlash] = useState(false);
    const prevKindRef = useRef(state.connection.kind);

    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    // Flash a brief "Reconnected" tick when the auto-reconnect loop hits home.
    useEffect(() => {
        const prev = prevKindRef.current;
        const curr = state.connection.kind;
        if (prev === "reconnecting" && curr === "connected") {
            setReconnectedFlash(true);
            const t = setTimeout(() => setReconnectedFlash(false), 1800);
            prevKindRef.current = curr;
            return () => clearTimeout(t);
        }
        prevKindRef.current = curr;
    }, [state.connection.kind]);

    const onHome = state.phase === "enter_room_code";

    if (!online) {
        // Device-level offline always wins — even if the WS state is stale.
        return (
            <Notice key="offline" tone="info" icon={<NoticeDot />}>
                You're offline
            </Notice>
        );
    }
    if (state.connection.kind === "reconnecting") {
        const { attempt } = state.connection;
        return (
            <Notice key="reconnecting" tone="info" icon={<NoticeSpinner />}>
                Reconnecting{attempt > 1 ? ` · ${attempt}` : "…"}
            </Notice>
        );
    }
    if (state.connection.kind === "failed" && !onHome) {
        return (
            <Notice
                key="failed"
                tone="error"
                actions={[{ label: "Try again", onClick: retryReconnect, primary: true }]}
                onDismiss={() => set("connection", { kind: "idle" })}
            >
                Couldn't reconnect
            </Notice>
        );
    }
    if (state.connection.kind === "kicked" && !onHome) {
        return (
            <Notice
                key="kicked"
                tone="error"
                onDismiss={() => set("connection", { kind: "idle" })}
            >
                Removed by host
            </Notice>
        );
    }
    if (reconnectedFlash) {
        return (
            <Notice key="reconnected" tone="success" icon={<NoticeDot tone="success" />}>
                Reconnected
            </Notice>
        );
    }
    return null;
}

export function AppNotices() {
    const connection = useConnectionNotice();

    return (
        <NoticeStack>
            <AnimatePresence>
                {connection}
            </AnimatePresence>
        </NoticeStack>
    );
}
